import   React              ,
       { useEffect          ,
         useMemo            ,
         useState          }  from "react";

import   classNames          from "classnames";

import   styles               from "./AdminActionLogList.module.scss";

import   TabularDisplay       from "../../widget/dataDisplay/tables/TabularDisplay";
import   AnimatedWaitingIcon2 from "../../widget/animations/animatedWaitingIcon2";
import   Field                from "../../widget/inputs/Field";
import   Select               from "../../widget/inputs/Select";

import { convertDateToString ,
         convertStringToDate }  from "../../../dataTypes/timeManagment/utils";

import { groupData          }  from "../../../dataTypes/dataSetManagment/regroupData";

import { AdminActionLogEntry } from "../../../dataTypes/adminActionLogManagment/types";

import { getAdminActionLogs } from "../../../backendServices/adminActionLogManagment/api";

const formatDate = (date: Date | null): string => date ? date.toLocaleString() : "—";

interface AdminActionLogListProps {
  adminId: number;
}

export const AdminActionLogList: React.FC<AdminActionLogListProps> = ({ adminId }) => {

  const [ logs           , setLogs           ] = useState<AdminActionLogEntry[]>([]);
  const [ showWaitingIcon, setShowWaitingIcon ] = useState(false);
  const [ ready          , setReady          ] = useState(false);

  const [ dateFrom       , setDateFrom       ] = useState("");
  const [ dateTo         , setDateTo         ] = useState("");
  const [ searchText     , setSearchText     ] = useState("");
  const [ actionFilter   , setActionFilter   ] = useState("");

  const actionOptions = useMemo(
    () => Array.from(new Set(logs.map(l => l.action).filter(Boolean))).sort(),
    [logs]
  );

  const filteredLogs = useMemo(() => {
    // Bornes en heure locale, la fin incluse jusqu'à 23:59:59.999.
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime()     : null;
    const to   = dateTo   ? new Date(`${dateTo}T23:59:59.999`).getTime()   : null;
    const needle = searchText.trim().toLowerCase();

    return logs.filter(l => {
      const t = l.actionDate.getTime();
      if (from !== null && t < from) return false;
      if (to   !== null && t > to  ) return false;
      if (actionFilter && l.action !== actionFilter) return false;
      if (needle) {
        const haystack = [l.module, l.action, l.result, l.elementLabel, l.comment, l.adminEmail, l.ipAddress]
          .join(" ").toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [logs, dateFrom, dateTo, searchText, actionFilter]);

  const hasFilter = !!(dateFrom || dateTo || searchText || actionFilter);
  const resetFilters = () => { setDateFrom(""); setDateTo(""); setSearchText(""); setActionFilter(""); };

  useEffect(() => {

    const fetchData = async () => {
      setShowWaitingIcon(true);

      const result = await getAdminActionLogs(adminId);

      if (result.code === 1) {
        setLogs(result.payload);
      }

      setShowWaitingIcon(false);
      setReady(true);
    };

    fetchData();
  }, [adminId]);

  const columns = [
    { header: "Date"      , accessor: "actionDate" as keyof AdminActionLogEntry , width: '150px' , cellRenderer: (value: any) => <span>{formatDate(value)}</span> },
    { header: "Module"    , accessor: "module"     as keyof AdminActionLogEntry , width: '180px' },
    { header: "Action"    , accessor: "action"     as keyof AdminActionLogEntry , width: '130px' },
    { header: "Résultat"  , accessor: "result"     as keyof AdminActionLogEntry , width: '110px' },
    { header: "Élément"   , accessor: "elementLabel" as keyof AdminActionLogEntry , width: '140px' },
    { header: "Commentaire", accessor: "comment"    as keyof AdminActionLogEntry },
    { header: "Auteur"    , accessor: "adminEmail"  as keyof AdminActionLogEntry , width: '220px' },
    { header: "IP"        , accessor: "ipAddress"  as keyof AdminActionLogEntry , width: '130px' , cellRenderer: (value: any) => <span>{value || '—'}</span> },
  ];

  const groupedLogs = groupData<AdminActionLogEntry>(filteredLogs, () => ({ key: '__all__' }));

  return (
    <div className={classNames(styles.root)}>
      {showWaitingIcon && <AnimatedWaitingIcon2 />}

      {
       ready &&
       (
        logs.length === 0
        ?
        (
         <div className={styles.noDataMessage}>
          Aucun log enregistré pour ce compte.
         </div>
        )
        :
        (
         <>
         {/* Même bandeau que les autres écrans (créateurs, chaînes, médias) : champs et sélecteurs
             de l'application, pas des <input> natifs. Les dates passent par le calendrier du
             dashboard ; l'état interne reste en yyyy-MM-dd. */}
         <div className={styles.upper_bar}>
           <Field type        = "text"
                  placeholder = "Rechercher : module, commentaire, auteur, IP…"
                  value       = {searchText}
                  width       = "280px"
                  onChange    = {(e) => setSearchText(e.target.value)} />

           <span className={styles.filterLabel}>Du</span>
           <Field type     = "calendar"
                  width    = "150px"
                  value    = {dateFrom ? convertDateToString(new Date(`${dateFrom}T00:00:00`), 'dd/MM/yyyy') : ''}
                  onChange = {(e) => { const d = convertStringToDate(e.target.value, 'dd/MM/yyyy'); setDateFrom(d ? convertDateToString(d, 'yyyy-MM-dd') : ''); }} />

           <span className={styles.filterLabel}>Au</span>
           <Field type     = "calendar"
                  width    = "150px"
                  value    = {dateTo ? convertDateToString(new Date(`${dateTo}T00:00:00`), 'dd/MM/yyyy') : ''}
                  onChange = {(e) => { const d = convertStringToDate(e.target.value, 'dd/MM/yyyy'); setDateTo(d ? convertDateToString(d, 'yyyy-MM-dd') : ''); }} />

           <Select choices         = {[ '' , ...actionOptions ]}
                   choicesLabel    = {[ 'Toutes les actions' , ...actionOptions ]}
                   value           = {actionFilter}
                   showPlaceholder = {false}
                   width           = "190px"
                   onChange        = {(e) => setActionFilter(e.target.value)} />

           {hasFilter && <button type="button" className={styles.filterReset} onClick={resetFilters}>Réinitialiser</button>}
           <span className={styles.filterCount}>{filteredLogs.length} / {logs.length}</span>
         </div>
         {
          filteredLogs.length === 0
          ?
          <div className={styles.noDataMessage}>Aucun log ne correspond à ces filtres.</div>
          :
         <div className={styles.tableSection}>
           <TabularDisplay
             groupedData      = {groupedLogs}
             columns          = {columns}
             showGroupHeaders = {false}
             defaultSort      = {{ column: 'actionDate', direction: 'desc' }}
           />
         </div>
         }
         </>
        )
       )
      }
    </div>
  );
};

export default AdminActionLogList;
